import { useCallback } from 'react'
import { useAdmin } from '../AdminContext'
import type { Json } from '../ui/ContentEditor'
import { withFreeShippingTier } from '../../core/rules/giftTiers'
import type { Policy, SiteSettings } from '../../core/types/site'

type NestedKey = 'subscription' | 'consult'

/**
 * Lezen en aanpassen van de instellingen in het concept, voor de tabbladen in admin/settings/.
 * Elke wijziging is een functionele update op de nieuwste stand, dus twee velden snel na
 * elkaar overschrijven elkaar niet.
 */
export function useSettings() {
  const { site, setSite } = useAdmin()

  const setPolicy = useCallback(
    <K extends keyof Policy>(key: K, value: Policy[K]) =>
      setSite((s) => {
        const next = { ...s, policy: { ...s.policy, [key]: value } }
        // Eén bron: de eerste cadeautrede ís gratis verzending en volgt het bedrag uit Verzending.
        return key === 'freeShippingFrom' && typeof value === 'number' ? { ...next, giftTiers: withFreeShippingTier(next.giftTiers, value) } : next
      }),
    [setSite],
  )

  const patchPolicy = useCallback(
    <K extends NestedKey>(key: K, patch: Partial<Policy[K]>) =>
      setSite((s) => ({ ...s, policy: { ...s.policy, [key]: { ...s.policy[key], ...patch } } })),
    [setSite],
  )

  /** Voor onderdelen die (nog) met de vrije lijsteditor werken: codes, cadeaustaffel, pers, salon. */
  const json = <K extends keyof SiteSettings>(key: K) => ({
    value: site[key] as unknown as Json,
    onChange: (next: Json) => setSite((s) => ({ ...s, [key]: next as unknown as SiteSettings[K] })),
  })

  return { site, setSite, policy: site.policy, setPolicy, patchPolicy, json }
}
